/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [true]*/

import { useLocalStorage } from '@finbeat/react-toolkit';
import useAuth from 'hooks/useAuth';
import {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGeneralSettingsContext } from './GeneralSettingsProvider';

const initialSidebarState = {
    favorites: [],
    state: false,
};

export type MenuItemType = {
    id?: string;
    value: string;
    icon: JSX.Element;
    link: string;
    translations?: string[];
    childs?: MenuItemType[];
    open?: boolean;
    parent?: string;
    role?: string;
    environment?: string[];
};

type SetSidebarStateType = {
    favorites: string[];
    state: boolean;
};

type SidebarContextType = {
    currentTab: string | null;
    menu: MenuItemType[];
    favoritesMenu: MenuItemType[];
    state: boolean;
    toggleState: () => void;
    filter: string;
    setFilter: (val: string) => void;
    itemIsFavorites: (id: string | undefined | null) => boolean;
    updateFavorites: (val: string) => void;
    setCurrentTab: (val: string) => void;
    accordionOpened: string | string[];
    setAccordionOpened: Dispatch<SetStateAction<string | string[]>>;
};

const SidebarContext = createContext<SidebarContextType>({} as SidebarContextType);

const SidebarProvider = ({
    mainMenu: defaultMenu,
    children,
}: React.PropsWithChildren<{ mainMenu: MenuItemType[] }>) => {
    const { t } = useTranslation();
    const { cultures } = useGeneralSettingsContext();
    const { auth, hasRole } = useAuth();
    const { pathname } = useLocation();

    const [filter, setFilter] = useState('');
    const [currentTab, setCurrentTab] = useState<string | undefined>(undefined);
    const [favorites, setFavorites] = useState<MenuItemType[]>([]);
    const [accordionOpened, setAccordionOpened] = useState<string | string[]>('');

    const [sidebarState, setSidebarState] = useLocalStorage<SetSidebarStateType>(
        'sidebar/state',
        initialSidebarState
    );

    const sidebarMenuItemList = useMemo<MenuItemType[]>(() => {
        const getTranslations = (value: string) => {
            return cultures.map((x) => t(value, { lng: x.code })?.toLowerCase());
        };

        const formatRolesList = (data: MenuItemType[]): MenuItemType[] => {
            return data
                .filter((x) => !x.role || hasRole(x.role))
                .map((x) => {
                    if (x.childs) {
                        const childs = formatRolesList(x.childs);
                        return { ...x, childs: childs, open: childs.some((x) => x.open) };
                    } else {
                        if (x.link === pathname) setCurrentTab(x.id);
                        return {
                            ...x,
                            translations: getTranslations(x.value),
                            open: x.link === pathname,
                        };
                    }
                });
        };

        return formatRolesList(defaultMenu);
    }, [t, hasRole, cultures, defaultMenu]);

    const filterMenu = (o: MenuItemType[]) => {
        return o.reduce((res: MenuItemType[], item) => {
            if (item.childs) {
                const childs = filterMenu(item.childs);
                childs.length > 0 && res.push({ ...item, childs, open: filter ? true : item.open });
            } else if (item.translations?.find((x) => x.includes(filter.toLowerCase())))
                res.push(item);

            return res;
        }, []);
    };

    const menu = useMemo(() => filterMenu(sidebarMenuItemList), [sidebarMenuItemList, filter]);
    const favoritesMenu = useMemo(() => filterMenu(favorites), [favorites, filter]);

    const getFavorites = (items: MenuItemType[], parent?: string) => {
        return items.reduce((res: MenuItemType[], x) => {
            if (
                x.id !== undefined &&
                sidebarState?.favorites.includes(x.id) &&
                x.translations?.some((y) => y.includes(filter.toLowerCase()))
            ) {
                res.push({ ...x, parent: parent });
            }

            x.childs && res.push(...getFavorites(x.childs, x.value));
            return res;
        }, []);
    };

    useEffect(() => setFavorites(getFavorites(sidebarMenuItemList)), [sidebarState?.favorites]);

    const itemIsFavorites = (id: string | undefined | null) =>
        id ? !!sidebarState?.favorites.includes(id) : false;

    const updateFavorites = (val: string) => {
        setSidebarState((prev) =>
            prev
                ? {
                    ...prev,
                    favorites: prev.favorites.includes(val)
                        ? prev.favorites.filter((x) => x !== val)
                        : [...prev.favorites, val],
                }
                : prev
        );
    };

    const toggleState = () =>
        setSidebarState((prev) => (prev ? { ...prev, state: !prev.state } : prev));

    useEffect(() => {
        !auth?.user && setCurrentTab('');
    }, [auth]);

    useEffect(() => {
        setAccordionOpened(filter ? ['menu', 'favorites', 'orders'] : '');
    }, [filter]);

    useEffect(() => {
        switch (pathname) {
            case '/':
                setAccordionOpened('');
                break;
            case '/workplacesettings': {
                setCurrentTab('workplacesettings');
                setAccordionOpened('');
                break;
            }
            case '/order':
                setAccordionOpened('orders');
                break;
            case '/merchant':
                setCurrentTab('merchant');
                setAccordionOpened('');
                break;
            case '/preorder':
                setCurrentTab('preorder');
                setAccordionOpened('');
                break;
            case '/clients':
                setCurrentTab('clients');
                setAccordionOpened('');
                break;
            default: {
                setAccordionOpened(
                    favorites.some((x) => x.link === pathname) ? 'favorites' : 'menu'
                );
                break;
            }
        }
    }, []);

    const contextValue = useMemo(
        () => ({
            currentTab: currentTab ?? null,
            favoritesMenu,
            filter,
            itemIsFavorites,
            menu,
            setCurrentTab,
            setFilter,
            state: !!sidebarState?.state,
            toggleState,
            updateFavorites,
            accordionOpened,
            setAccordionOpened,
        }),
        [
            currentTab,
            favoritesMenu,
            filter,
            itemIsFavorites,
            menu,
            setCurrentTab,
            setFilter,
            sidebarState?.state,
            toggleState,
            updateFavorites,
            accordionOpened,
            setAccordionOpened,
        ]
    );

    return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};

const useSidebarContext = () => useContext<SidebarContextType>(SidebarContext);

export { SidebarProvider, useSidebarContext };
